import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import { trackPageView } from '../analytics';
import { currentUser, hasValidSession, clearUserSession } from '../session';
import { policies } from '../data/policies';

const siteUrl = 'https://epraggies.rasspl.co.za';
const siteName = 'EP Raggies';
const defaultTitle = 'EP Raggies | Eastern Province RASSPL Franchise';
const defaultDescription = 'EP Raggies is the Eastern Province franchise of RASSPL Africa, with local rock and surf competitions, venues, leaderboards, rules and catch documentation guidance.';
const defaultImageAlt = 'High quality EP Raggies logo for the RASSPL Eastern Province franchise';
const privateRouteMeta = {
  requiresAuth: true,
  robots: 'noindex, nofollow',
  title: 'Member Area | EP Raggies',
  description: 'Secure EP Raggies member and administrator area.',
};

const routes = [
  {
    path: '/policies',
    name: 'policies',
    component: () => import('../views/PoliciesView.vue'),
    meta: { title: 'Policies & Member Information | EP Raggies', description: 'Privacy, cookies, membership terms, safety and help with your rights at EP Raggies.' },
  },
  ...policies.map(policy => ({
    path: `/policies/${policy.slug}`,
    name: policy.slug,
    component: () => import('../views/PolicyView.vue'),
    meta: { policySlug: policy.slug, title: `${policy.title} | EP Raggies`, description: policy.summary },
  })),
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: defaultTitle,
      description: defaultDescription,
    },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('../views/AboutView.vue'),
    meta: {
      title: 'About EP Raggies | Eastern Province RASSPL Rock and Surf',
      description: 'Learn about EP Raggies, the Eastern Province RASSPL Africa franchise for catch, photograph and release rock and surf anglers.',
    },
  },
  {
    path: '/rules',
    name: 'rules',
    component: () => import('../views/RulesView.vue'),
    meta: {
      title: 'EP Raggies Rules | RASSPL Africa Competition Rules',
      description: 'Read the EP Raggies public competition rules for RASSPL Africa registration, catch photos, fish handling, scoring, boundaries and prize eligibility.',
    },
  },
  {
    path: '/competitions',
    name: 'competitions',
    component: () => import('../views/CompetitionsView.vue'),
    meta: {
      title: 'EP Raggies Competitions | Eastern Province Fishing Venues',
      description: 'View EP Raggies competition dates, Eastern Province fishing venues, tide information and RASSPL Africa event details for the season.',
    },
  },
  {
    path: '/document-fish',
    name: 'document-fish',
    component: () => import('../views/FishDocumentationView.vue'),
    meta: {
      title: 'How to Document a Fish | EP Raggies Catch Photos',
      description: 'Follow the EP Raggies fish documentation steps for RASSPL catch, photograph and release angling with bucket, mat and angler photos.',
    },
  },
  {
    path: '/fish-calculator',
    name: 'fish-calculator',
    component: () => import('../views/FishCalculatorView.vue'),
    meta: {
      title: 'Fish Weight Calculator South Africa | Kob, Garrick, Shad | EP Raggies',
      description: 'Convert fish length to weight in South Africa. Estimate kob, cob, garrick, leervis, shad, elf, galjoen, steenbras, grunter, musselcracker, yellowtail, blacktail and more from centimetres.',
      keywords: 'fish weight calculator South Africa, fish length to weight calculator, fish weight from length, fish weight from cm, kob length to weight, cob length to weight, dusky kob weight calculator, garrick length to weight, leervis weight calculator, shad length to weight, elf fish weight calculator, galjoen length to weight, steenbras length to weight, white steenbras weight calculator, grunter length to weight, spotted grunter weight calculator, musselcracker length to weight, black musselcracker, poenskop, yellowtail weight calculator, blacktail length to weight, South African fishing calculator, RASSPL fish calculator',
    },
  },
  {
    path: '/sponsors/',
    name: 'sponsors',
    component: () => import('../views/SponsorsView.vue'),
    meta: {
      title: 'Sponsors | EP Raggies',
      description: 'Meet the local businesses and partners sponsoring EP Raggies rock and surf competitions in the Eastern Province.',
    },
  },
  {
    path: '/sponsors',
    redirect: '/sponsors/',
  },
  {
    path: '/announcements',
    name: 'announcements',
    component: () => import('../views/AnnouncementsView.vue'),
    meta: {
      title: 'Announcements | EP Raggies',
      description: 'Latest EP Raggies announcements, photos, videos, competition notices and community updates.',
    },
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('../views/ContactView.vue'),
    meta: {
      title: 'Contact EP Raggies | Eastern Province RASSPL',
      description: 'Contact EP Raggies for RASSPL Eastern Province competition, membership, sponsor and catch documentation queries.',
    },
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      ...privateRouteMeta,
      requiresAuth: false,
      title: 'Login | EP Raggies',
      description: 'Log in to the secure EP Raggies member portal.',
    },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('../views/SignUpView.vue'),
    meta: {
      title: 'Join EP Raggies | RASSPL Eastern Province Signup',
      description: 'Sign up for EP Raggies membership and join the Eastern Province RASSPL rock and surf angling season.',
    },
  },
  {
    path: '/admin/dashboard',
    name: 'admin-dashboard',
    component: () => import('../views/AdminDashboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/members',
    name: 'admin-members',
    component: () => import('../views/AdminMembersView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/teams',
    name: 'admin-teams',
    component: () => import('../views/AdminTeamsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/species',
    name: 'admin-species',
    component: () => import('../views/AdminSpeciesView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/competitions',
    name: 'admin-competitions',
    component: () => import('../views/AdminCompetitionsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/catch-reviews',
    name: 'admin-catch-reviews',
    component: () => import('../views/AdminCatchReviewsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/prize-templates',
    name: 'admin-prize-templates',
    component: () => import('../views/AdminPrizeTemplatesView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/member-leaderboard',
    name: 'admin-member-leaderboard',
    component: () => import('../views/AdminMemberLeaderboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/species-leaderboard',
    name: 'admin-species-leaderboard',
    component: () => import('../views/AdminSpeciesLeaderboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/unsorted-uploads',
    name: 'admin-unsorted-uploads',
    component: () => import('../views/AdminUnsortedUploadsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/analytics',
    name: 'admin-analytics',
    component: () => import('../views/AdminAnalyticsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/sponsors',
    name: 'admin-sponsors',
    component: () => import('../views/AdminSponsorsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/admin/announcements',
    name: 'admin-announcements',
    component: () => import('../views/AdminAnnouncementsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/dashboard',
    name: 'member-dashboard',
    component: () => import('../views/MemberDashboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/catch-uploads',
    name: 'member-catch-uploads',
    component: () => import('../views/MemberCatchUploadsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/unsorted-uploads',
    name: 'member-unsorted-uploads',
    component: () => import('../views/MemberUnsortedUploadsView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/scorecard-history',
      name: 'member-scorecard-history',
      component: () => import('../views/MemberScorecardHistoryView.vue'),
      meta: privateRouteMeta,
    },
    {
      path: '/member/gallery',
    name: 'member-gallery',
    component: () => import('../views/MemberGalleryView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/leaderboard',
    name: 'member-leaderboard',
    component: () => import('../views/MemberLeaderboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/team-leaderboard',
    name: 'member-team-leaderboard',
    component: () => import('../views/MemberTeamLeaderboardView.vue'),
    meta: privateRouteMeta,
  },
  {
    path: '/member/profile',
    name: 'member-profile',
    component: () => import('../views/MemberProfileView.vue'),
    meta: privateRouteMeta,
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    return { top: 0 };
  },
});

router.beforeEach((to) => {
  if (to.meta.requiresAuth && !hasValidSession()) {
    const hadSession = !!currentUser();
    clearUserSession();
    return { name: 'login', query: hadSession ? { reason: 'expired' } : {} };
  }
});

router.afterEach((to) => {
  const shareImage = `${window.location.origin}/share-card.png`;
  const title = to.meta?.title || defaultTitle;
  const description = to.meta?.description || defaultDescription;
  const robots = to.meta?.robots || 'index, follow';
  const canonicalUrl = canonicalForRoute(to);

  document.title = title;
  setMeta('description', description);
  setMeta('robots', robots);
  setMeta('keywords', to.meta?.keywords || 'EP Raggies, RASSPL EP Raggies, Eastern Province RASSPL, RASSPL Africa franchise, Rock and Surf Super Pro League, shore angling, Eastern Cape fishing');
  setMeta('og:title', title, 'property');
  setMeta('og:description', description, 'property');
  setMeta('og:url', canonicalUrl, 'property');
  setMeta('og:type', 'website', 'property');
  setMeta('og:site_name', siteName, 'property');
  setMeta('og:image', shareImage, 'property');
  setMeta('og:image:secure_url', shareImage, 'property');
  setMeta('og:image:width', '1200', 'property');
  setMeta('og:image:height', '630', 'property');
  setMeta('og:image:alt', defaultImageAlt, 'property');
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  setMeta('twitter:image', shareImage);
  setMeta('twitter:image:alt', defaultImageAlt);
  setCanonical(canonicalUrl);
  trackPageView(to);
});

function setMeta(name, content, key = 'name') {
  let element = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(key, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function setCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function canonicalForRoute(route) {
  const path = route.path === '/sponsors/' ? '/sponsors/' : route.path.replace(/\/$/, '') || '/';
  return `${window.location.origin || siteUrl}${path}`;
}

export default router;
